import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Plus, Users, Shield, Building2, Globe, Settings, FileText, Activity } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const TABS = [
  { id: "admins", label: "Admins", icon: Users },
  { id: "roles", label: "Roles", icon: Shield },
  { id: "departments", label: "Departments", icon: Building2 },
  { id: "master", label: "Master Data", icon: Globe },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "logs", label: "Activity Logs", icon: Activity },
];

export const EnterpriseAdmin = () => {
  const [tab, setTab] = useState("admins");
  const [data, setData] = useState({ admins: [], roles: [], departments: [], countries: [], languages: [], settings: [], logs: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [admins, roles, departments, countries, languages, settings, logs] = await Promise.all([
        axios.get(`${API}/admin/enterprise/admins`, headers()),
        axios.get(`${API}/admin/enterprise/roles`, headers()),
        axios.get(`${API}/admin/enterprise/departments`, headers()),
        axios.get(`${API}/admin/enterprise/master/countries`, headers()),
        axios.get(`${API}/admin/enterprise/master/languages`, headers()),
        axios.get(`${API}/admin/enterprise/settings`, headers()),
        axios.get(`${API}/admin/enterprise/activity-logs?limit=50`, headers()),
      ]);
      setData({
        admins: admins.data.admins,
        roles: roles.data.roles,
        departments: departments.data.departments,
        countries: countries.data.countries,
        languages: languages.data.languages,
        settings: settings.data.settings,
        logs: logs.data.logs,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(load, []);

  const create = async (endpoint, payload, key) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/enterprise/${endpoint}`, payload, headers());
      toast.success(`${key} created`);
      setForm({});
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoaderCircle className="mx-auto mt-10 h-6 w-6 animate-spin text-[#00D4FF]" />;

  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="enterprise-admin">
      <p className="font-display text-lg font-bold">🏛️ Enterprise Admin</p>
      <p className="mt-1 text-xs text-slate-500">Admin schema · roles · departments · master data · settings · audit</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`ea-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 font-jbmono text-[10px] font-bold uppercase tracking-wider ${tab === t.id ? "bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] text-white" : "border border-white/10 text-slate-400"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "admins" && (
          <div>
            <div className="grid gap-2 sm:grid-cols-3">
              <input data-testid="ea-admin-code" value={form.employee_code || ""} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} placeholder="Employee code" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-admin-name" value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-admin-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-admin-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
            </div>
            <button data-testid="ea-admin-create" onClick={() => create("admins", form, "Admin")} disabled={busy || !form.employee_code || !form.full_name || !form.email || !form.password}
              className="mt-3 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add admin
            </button>
            <div className="mt-4 rounded-2xl border border-dashed border-white/15 p-4">
              <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Bulk import (CSV)</p>
              <p className="mt-1 text-xs text-slate-400">Columns: employee_code, full_name, email, password</p>
              <textarea data-testid="ea-bulk-csv" value={form.bulk_csv || ""} onChange={(e) => setForm({ ...form, bulk_csv: e.target.value })} rows={4}
                placeholder="EMP-002,Sara Chen,sara.ops@funz.store,SaraOps@2026"
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#09090B]/80 px-3.5 py-2.5 font-jbmono text-xs text-white outline-none focus:border-[#00D4FF]/60" />
              <button data-testid="ea-bulk-import" onClick={async () => {
                setBusy(true);
                try {
                  const { data } = await axios.post(`${API}/admin/enterprise/admins/bulk-import`, form.bulk_csv, { ...headers(), headers: { ...headers().headers, "Content-Type": "text/csv" } });
                  toast.success(`${data.created} admins imported${data.errors.length ? `, ${data.errors.length} skipped` : ""}`);
                  setForm({ ...form, bulk_csv: "" });
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                } finally {
                  setBusy(false);
                }
              }} disabled={busy || !form.bulk_csv}
                className="mt-2 rounded-full border border-[#00D4FF]/40 px-4 py-2 font-jbmono text-[10px] font-bold uppercase tracking-wider text-[#00D4FF] disabled:opacity-50">
                Import CSV
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {data.admins.map((a) => (
                <div key={a.id} data-testid={`ea-admin-${a.id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold">{a.full_name}</p>
                    <p className="truncate font-jbmono text-[10px] text-slate-500">{a.employee_code} · {a.email}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                    a.status === "ACTIVE" ? "bg-[#22C55E]/10 text-[#22C55E]" : a.status === "SUSPENDED" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F97316]/10 text-[#F97316]"
                  }`}>{a.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "roles" && (
          <div>
            <div className="grid gap-2 sm:grid-cols-3">
              <input data-testid="ea-role-name" value={form.role_name || ""} onChange={(e) => setForm({ ...form, role_name: e.target.value })} placeholder="Role name" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-role-desc" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-role-level" type="number" value={form.level || 0} onChange={(e) => setForm({ ...form, level: parseInt(e.target.value, 10) || 0 })} placeholder="Level" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
            </div>
            <button data-testid="ea-role-create" onClick={() => create("roles", form, "Role")} disabled={busy || !form.role_name}
              className="mt-3 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add role
            </button>
            <div className="mt-4 space-y-2">
              {data.roles.map((r) => (
                <div key={r.id} data-testid={`ea-role-${r.id.slice(-6)}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold">{r.role_name}</p>
                    <p className="truncate font-jbmono text-[10px] text-slate-500">{r.description}</p>
                  </div>
                  <span className="rounded-full bg-[#00D4FF]/10 px-2.5 py-1 font-jbmono text-[9px] font-bold text-[#00D4FF]">Level {r.level}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "departments" && (
          <div>
            <div className="grid gap-2 sm:grid-cols-2">
              <input data-testid="ea-dept-name" value={form.department_name || ""} onChange={(e) => setForm({ ...form, department_name: e.target.value })} placeholder="Department name" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-dept-desc" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
            </div>
            <button data-testid="ea-dept-create" onClick={() => create("departments", form, "Department")} disabled={busy || !form.department_name}
              className="mt-3 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add department
            </button>
            <div className="mt-4 space-y-2">
              {data.departments.map((d) => (
                <div key={d.id} data-testid={`ea-dept-${d.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
                  <p className="text-xs font-bold">{d.department_name}</p>
                  <p className="mt-0.5 font-jbmono text-[10px] text-slate-500">{d.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "master" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="font-display text-sm font-bold">Countries ({data.countries.length})</p>
              <div className="mt-2 space-y-2">
                {data.countries.map((c) => (
                  <div key={c.id} className="rounded-xl border border-white/10 bg-[#09090B]/80 p-3">
                    <p className="text-xs font-bold">{c.country_name}</p>
                    <p className="font-jbmono text-[10px] text-slate-500">{c.country_code} · {c.currency_code} · {c.timezone}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="font-display text-sm font-bold">Languages ({data.languages.length})</p>
              <div className="mt-2 space-y-2">
                {data.languages.map((l) => (
                  <div key={l.id} className="rounded-xl border border-white/10 bg-[#09090B]/80 p-3">
                    <p className="text-xs font-bold">{l.language_name}</p>
                    <p className="font-jbmono text-[10px] text-slate-500">{l.language_code} · {l.is_active ? "Active" : "Inactive"}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div>
            <div className="grid gap-2 sm:grid-cols-3">
              <input data-testid="ea-set-key" value={form.setting_key || ""} onChange={(e) => setForm({ ...form, setting_key: e.target.value })} placeholder="Setting key" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-set-value" value={form.setting_value || ""} onChange={(e) => setForm({ ...form, setting_value: e.target.value })} placeholder="Value" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
              <input data-testid="ea-set-group" value={form.setting_group || "general"} onChange={(e) => setForm({ ...form, setting_group: e.target.value })} placeholder="Group" className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm outline-none focus:border-[#00D4FF]/60" />
            </div>
            <button data-testid="ea-set-create" onClick={() => create("settings", form, "Setting")} disabled={busy || !form.setting_key}
              className="mt-3 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Save setting
            </button>
            <div className="mt-4 space-y-2">
              {data.settings.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#09090B]/80 px-3.5 py-2.5">
                  <span className="font-jbmono text-[10px] text-slate-400">{s.setting_key}</span>
                  <span className="min-w-0 flex-1 truncate text-xs">{s.setting_value}</span>
                  <span className="font-jbmono text-[9px] text-slate-600">{s.setting_group}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "logs" && (
          <div className="space-y-1.5">
            {data.logs.map((l) => (
              <div key={l.id} data-testid={`ea-log-${l.id.slice(-6)}`} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#09090B]/60 px-3.5 py-2.5">
                <span className="rounded-full bg-[#7B61FF]/10 px-2.5 py-0.5 font-jbmono text-[9px] font-bold text-[#7B61FF]">{l.action_type}</span>
                <p className="min-w-0 flex-1 truncate font-jbmono text-[10px] text-slate-500">{l.module_name} · {l.target_table}</p>
                <span className="font-jbmono text-[9px] text-slate-600">{String(l.created_at).slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
